import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Add,
  AssignmentTurnedIn,
  Delete,
  Edit,
  Event,
  FilterAlt,
  Search,
  Star,
  StarBorder,
  TaskAlt,
  WarningAmber
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { differenceInCalendarDays, format, isValid, parseISO } from 'date-fns';

type Priority = 'low' | 'medium' | 'high';
type TodoStatus = 'pending' | 'in_progress' | 'completed' | 'blocked';
type DueFilter = 'all' | 'overdue' | 'today' | 'upcoming' | 'no_due';
type QuickView = 'all' | 'today' | 'overdue' | 'completed' | 'important';
type SortBy = 'updated' | 'due' | 'priority';

interface TodoItem {
  id: string;
  title: string;
  notes: string;
  priority: Priority;
  status: TodoStatus;
  dueDate: string | null;
  important: boolean;
  createdAt: string;
  updatedAt: string;
}

interface LegacyTodo {
  id: string;
  text: string;
  completed?: boolean;
  priority?: Priority;
  dueDate?: string | null;
  important?: boolean;
  createdAt?: string;
}

type TodoForm = Omit<TodoItem, 'id' | 'createdAt' | 'updatedAt'>;

const STORAGE_KEY = 'mfs_todo_items_v3';
const LEGACY_TODO_KEY = 'todos';

const PRIORITY_COLOR: Record<Priority, 'success' | 'warning' | 'error'> = {
  low: 'success',
  medium: 'warning',
  high: 'error',
};

const PRIORITY_RANK: Record<Priority, number> = { high: 1, medium: 2, low: 3 };

const STATUS_COLOR: Record<TodoStatus, 'default' | 'info' | 'success' | 'error'> = {
  pending: 'default',
  in_progress: 'info',
  completed: 'success',
  blocked: 'error',
};

const defaultForm = (): TodoForm => ({
  title: '',
  notes: '',
  priority: 'medium',
  status: 'pending',
  dueDate: null,
  important: false,
});

const safeParse = <T,>(raw: string | null): T[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
};

const toStorageDate = (value: Date): string => format(value, 'yyyy-MM-dd');

const normalizeDate = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const parsed = parseISO(value);
  return isValid(parsed) ? toStorageDate(parsed) : null;
};

const dueBucket = (dueDate: string | null): DueFilter => {
  if (!dueDate) return 'no_due';
  const parsed = parseISO(dueDate);
  if (!isValid(parsed)) return 'no_due';
  const days = differenceInCalendarDays(parsed, new Date());
  if (days < 0) return 'overdue';
  if (days === 0) return 'today';
  return 'upcoming';
};

const dueMeta = (dueDate: string | null): { label: string; color: 'default' | 'info' | 'warning' | 'error' } => {
  if (!dueDate) return { label: 'No due date', color: 'default' };
  const parsed = parseISO(dueDate);
  if (!isValid(parsed)) return { label: 'No due date', color: 'default' };
  const days = differenceInCalendarDays(parsed, new Date());
  if (days < 0) return { label: `Overdue by ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'}`, color: 'error' };
  if (days === 0) return { label: 'Due today', color: 'warning' };
  return { label: `Due ${format(parsed, 'dd-MM-yyyy')}`, color: 'info' };
};

const mapLegacyTodo = (todo: LegacyTodo): TodoItem => {
  const now = todo.createdAt || new Date().toISOString();
  return {
    id: String(todo.id || Date.now()),
    title: todo.text || 'Untitled task',
    notes: '',
    priority: todo.priority || 'medium',
    status: todo.completed ? 'completed' : 'pending',
    dueDate: normalizeDate(todo.dueDate),
    important: Boolean(todo.important),
    createdAt: now,
    updatedAt: now,
  };
};

const statusLabel = (status: TodoStatus): string => {
  if (status === 'in_progress') return 'In Progress';
  if (status === 'completed') return 'Completed';
  if (status === 'blocked') return 'Blocked';
  return 'Pending';
};

const TodoApp: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tasks, setTasks] = useState<TodoItem[]>([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<TodoForm>(defaultForm);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | TodoStatus>('all');
  const [priorityFilter, setPriorityFilter] = useState<'all' | Priority>('all');
  const [dueFilter, setDueFilter] = useState<DueFilter>('all');
  const [sortBy, setSortBy] = useState<SortBy>('updated');
  const [quickView, setQuickView] = useState<QuickView>('all');

  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    const saved = safeParse<TodoItem>(localStorage.getItem(STORAGE_KEY));
    if (saved.length > 0) return setTasks(saved);
    const oldTodos = safeParse<LegacyTodo>(localStorage.getItem(LEGACY_TODO_KEY));
    if (oldTodos.length > 0) {
      const migrated = oldTodos.map(mapLegacyTodo);
      setTasks(migrated);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated));
      setSnack({ open: true, message: 'Legacy todos migrated.', severity: 'info' });
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tasks));
  }, [tasks]);

  const stats = useMemo(() => ({
    total: tasks.length,
    today: tasks.filter((t) => dueBucket(t.dueDate) === 'today').length,
    overdue: tasks.filter((t) => dueBucket(t.dueDate) === 'overdue').length,
    completed: tasks.filter((t) => t.status === 'completed').length,
    important: tasks.filter((t) => t.important).length,
  }), [tasks]);

  const filteredTasks = useMemo(() => {
    const byQuickView = tasks.filter((task) => {
      if (quickView === 'today') return dueBucket(task.dueDate) === 'today';
      if (quickView === 'overdue') return dueBucket(task.dueDate) === 'overdue';
      if (quickView === 'completed') return task.status === 'completed';
      if (quickView === 'important') return task.important;
      return true;
    });

    const byFilters = byQuickView.filter((task) => {
      const q = search.trim().toLowerCase();
      const matchesSearch = q ? task.title.toLowerCase().includes(q) || task.notes.toLowerCase().includes(q) : true;
      const matchesStatus = statusFilter === 'all' ? true : task.status === statusFilter;
      const matchesPriority = priorityFilter === 'all' ? true : task.priority === priorityFilter;
      const matchesDue = dueFilter === 'all' ? true : dueBucket(task.dueDate) === dueFilter;
      return matchesSearch && matchesStatus && matchesPriority && matchesDue;
    });

    return [...byFilters].sort((a, b) => {
      if (sortBy === 'priority') return PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];
      if (sortBy === 'due') {
        const aDue = a.dueDate ? parseISO(a.dueDate).getTime() : Number.POSITIVE_INFINITY;
        const bDue = b.dueDate ? parseISO(b.dueDate).getTime() : Number.POSITIVE_INFINITY;
        return aDue - bDue;
      }
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  }, [tasks, quickView, search, statusFilter, priorityFilter, dueFilter, sortBy]);

  const resetForm = () => {
    setForm(defaultForm());
    setEditId(null);
    setOpen(false);
  };

  const saveTask = () => {
    if (!form.title.trim()) {
      return setSnack({ open: true, message: 'Task title is required.', severity: 'error' });
    }
    const now = new Date().toISOString();
    const payload: TodoForm = {
      ...form,
      title: form.title.trim(),
      notes: form.notes.trim(),
      dueDate: normalizeDate(form.dueDate),
    };
    if (editId) {
      setTasks((prev) => prev.map((t) => (t.id === editId ? { ...t, ...payload, updatedAt: now } : t)));
      setSnack({ open: true, message: 'Task updated.', severity: 'success' });
    } else {
      const newTask: TodoItem = {
        id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        ...payload,
        createdAt: now,
        updatedAt: now,
      };
      setTasks((prev) => [newTask, ...prev]);
      setSnack({ open: true, message: 'Task created.', severity: 'success' });
    }
    resetForm();
  };

  const editTask = (task: TodoItem) => {
    setEditId(task.id);
    setForm({
      title: task.title,
      notes: task.notes,
      priority: task.priority,
      status: task.status,
      dueDate: task.dueDate,
      important: task.important,
    });
    setOpen(true);
  };

  const setTaskStatus = (id: string, status: TodoStatus) => {
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, status, updatedAt: new Date().toISOString() } : t)));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Paper elevation={0} sx={{ mb: 3, p: { xs: 2.5, md: 3.5 }, borderRadius: 3, color: 'common.white', background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 65%, ${theme.palette.secondary.main} 100%)` }}>
          <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 700 }} gutterBottom>Robust Todo Board</Typography>
          <Typography variant="body1" sx={{ opacity: 0.92 }}>General-purpose task tracking with priorities, due dates, and quick filters.</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
            <Chip label={`${stats.total} Total`} sx={{ bgcolor: alpha('#ffffff', 0.22), color: 'common.white' }} />
            <Chip label={`${stats.today} Due Today`} sx={{ bgcolor: alpha('#ffffff', 0.22), color: 'common.white' }} />
            <Chip label={`${stats.overdue} Overdue`} sx={{ bgcolor: alpha('#ffffff', 0.22), color: 'common.white' }} />
            <Chip label={`${stats.completed} Completed`} sx={{ bgcolor: alpha('#ffffff', 0.22), color: 'common.white' }} />
          </Box>
        </Paper>

        <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.8)}` }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}><FilterAlt sx={{ mr: 1 }} />Filters</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr 1fr 1fr 1fr auto', gap: 1.2 }}>
            <TextField size="small" label="Search title or notes" value={search} onChange={(e) => setSearch(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
            <FormControl size="small"><InputLabel>Status</InputLabel><Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as 'all' | TodoStatus)}><MenuItem value="all">All</MenuItem><MenuItem value="pending">Pending</MenuItem><MenuItem value="in_progress">In Progress</MenuItem><MenuItem value="completed">Completed</MenuItem><MenuItem value="blocked">Blocked</MenuItem></Select></FormControl>
            <FormControl size="small"><InputLabel>Priority</InputLabel><Select label="Priority" value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value as 'all' | Priority)}><MenuItem value="all">All</MenuItem><MenuItem value="high">High</MenuItem><MenuItem value="medium">Medium</MenuItem><MenuItem value="low">Low</MenuItem></Select></FormControl>
            <FormControl size="small"><InputLabel>Due</InputLabel><Select label="Due" value={dueFilter} onChange={(e) => setDueFilter(e.target.value as DueFilter)}><MenuItem value="all">All</MenuItem><MenuItem value="overdue">Overdue</MenuItem><MenuItem value="today">Today</MenuItem><MenuItem value="upcoming">Upcoming</MenuItem><MenuItem value="no_due">No Due Date</MenuItem></Select></FormControl>
            <FormControl size="small"><InputLabel>Sort</InputLabel><Select label="Sort" value={sortBy} onChange={(e) => setSortBy(e.target.value as SortBy)}><MenuItem value="updated">Recently Updated</MenuItem><MenuItem value="due">Due Date</MenuItem><MenuItem value="priority">Priority</MenuItem></Select></FormControl>
            <FormControl size="small"><InputLabel>Quick View</InputLabel><Select label="Quick View" value={quickView} onChange={(e) => setQuickView(e.target.value as QuickView)}><MenuItem value="all">All</MenuItem><MenuItem value="today">Today</MenuItem><MenuItem value="overdue">Overdue</MenuItem><MenuItem value="completed">Completed</MenuItem><MenuItem value="important">Important</MenuItem></Select></FormControl>
            <Button variant="contained" startIcon={<Add />} onClick={() => setOpen(true)}>Add</Button>
          </Box>
          <Typography sx={{ mt: 1 }} variant="body2" color="text.secondary">Showing {filteredTasks.length} tasks</Typography>
        </Paper>

        {filteredTasks.length ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(340px, 1fr))', gap: 2.5 }}>
            {filteredTasks.map((task) => {
              const due = dueMeta(task.dueDate);
              return (
                <Card key={task.id} sx={{ borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.85)}` }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>{task.title}</Typography>
                      <Box>
                        <Tooltip title={task.important ? 'Remove important' : 'Mark important'}><IconButton size="small" onClick={() => setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, important: !t.important, updatedAt: new Date().toISOString() } : t)))}>{task.important ? <Star color="warning" /> : <StarBorder />}</IconButton></Tooltip>
                        <Tooltip title="Edit"><IconButton size="small" onClick={() => editTask(task)}><Edit fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => window.confirm('Delete this task?') && setTasks((prev) => prev.filter((t) => t.id !== task.id))}><Delete fontSize="small" /></IconButton></Tooltip>
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.8, mb: 1 }}>{task.notes || 'No notes added.'}</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.2 }}>
                      <Chip size="small" label={task.priority} color={PRIORITY_COLOR[task.priority]} />
                      <Chip size="small" label={statusLabel(task.status)} color={STATUS_COLOR[task.status]} />
                      <Chip size="small" icon={<Event fontSize="small" />} label={due.label} color={due.color} variant="outlined" />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button size="small" variant={task.status === 'pending' ? 'contained' : 'outlined'} onClick={() => setTaskStatus(task.id, 'pending')}>Pending</Button>
                      <Button size="small" color="info" variant={task.status === 'in_progress' ? 'contained' : 'outlined'} onClick={() => setTaskStatus(task.id, 'in_progress')}>Progress</Button>
                      <Button size="small" color="success" variant={task.status === 'completed' ? 'contained' : 'outlined'} startIcon={<TaskAlt />} onClick={() => setTaskStatus(task.id, 'completed')}>Done</Button>
                      <Button size="small" color="error" variant={task.status === 'blocked' ? 'contained' : 'outlined'} startIcon={<WarningAmber />} onClick={() => setTaskStatus(task.id, 'blocked')}>Blocked</Button>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        ) : (
          <Paper elevation={0} sx={{ p: 5, textAlign: 'center', borderRadius: 3, border: `1px dashed ${alpha(theme.palette.divider, 0.9)}` }}>
            <AssignmentTurnedIn sx={{ fontSize: 46, color: 'text.disabled', mb: 1 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>No tasks found</Typography>
            <Button variant="contained" startIcon={<Add />} onClick={() => setOpen(true)}>Add Task</Button>
          </Paper>
        )}

        <Dialog open={open} onClose={resetForm} fullWidth maxWidth="sm" fullScreen={isMobile}>
          <DialogTitle sx={{ bgcolor: theme.palette.primary.main, color: 'common.white' }}>{editId ? 'Edit Task' : 'Add Task'}</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2, display: 'grid', gap: 2 }}>
              <TextField size="small" required label="Task title" value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} />
              <TextField size="small" multiline minRows={3} label="Notes" value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} />
              <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 2 }}>
                <FormControl size="small"><InputLabel>Priority</InputLabel><Select label="Priority" value={form.priority} onChange={(e) => setForm((p) => ({ ...p, priority: e.target.value as Priority }))}><MenuItem value="high">High</MenuItem><MenuItem value="medium">Medium</MenuItem><MenuItem value="low">Low</MenuItem></Select></FormControl>
                <FormControl size="small"><InputLabel>Status</InputLabel><Select label="Status" value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value as TodoStatus }))}><MenuItem value="pending">Pending</MenuItem><MenuItem value="in_progress">In Progress</MenuItem><MenuItem value="completed">Completed</MenuItem><MenuItem value="blocked">Blocked</MenuItem></Select></FormControl>
              </Box>
              <DatePicker label="Due date" value={form.dueDate ? parseISO(form.dueDate) : null} onChange={(value) => setForm((p) => ({ ...p, dueDate: value ? toStorageDate(value) : null }))} format="dd-MM-yyyy" slotProps={{ textField: { size: 'small', fullWidth: true } }} />
              <Box sx={{ display: 'flex', alignItems: 'center' }}><Checkbox checked={form.important} onChange={(e) => setForm((p) => ({ ...p, important: e.target.checked }))} icon={<StarBorder />} checkedIcon={<Star color="warning" />} /><Typography variant="body2">Mark as important</Typography></Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button variant="outlined" onClick={resetForm}>Cancel</Button>
            <Button variant="contained" onClick={saveTask}>{editId ? 'Update' : 'Save'} Task</Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack((p) => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
          <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((p) => ({ ...p, open: false }))}>{snack.message}</Alert>
        </Snackbar>

        {isMobile && <Fab color="primary" sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: theme.zIndex.speedDial }} onClick={() => setOpen(true)}><Add /></Fab>}
      </Container>
    </LocalizationProvider>
  );
};

export default TodoApp;
